/**
 * 로컬 계측 이벤트 스키마 (SPEC S-10).
 *
 * 명세의 요구 두 가지를 **주석이 아니라 규칙으로** 강제한다.
 *
 * 1. "UID·개인정보·자유 입력 텍스트를 이벤트에 넣지 않는다."
 *    페이로드 값으로 임의의 문자열을 허용하면 언젠가 누군가 닉네임이나
 *    자유 입력을 넣는다. 그래서 값 타입을 숫자와 불리언으로 제한하고,
 *    문자열은 `stage_id` 하나만, 그것도 정해진 패턴에 맞을 때만 받는다.
 *    새 문자열 필드를 넣으려면 이 파일을 고쳐야 하고, 그러면 리뷰에 걸린다.
 *
 * 2. "개별 `move`는 이벤트로 남기지 않고 `stage_end`에 요약값만 포함."
 *    이벤트 이름 목록에 `move`가 없고, 목록 밖 이름은 거부한다.
 */

/** SPEC S-10이 열거한 이벤트. 이 목록 밖의 이름은 만들 수 없다. */
export const TELEMETRY_EVENT_NAMES = [
  'app_open',
  'how_to_play_view',
  'how_to_play_complete',
  'how_to_play_skip',
  'tutorial_start',
  'tutorial_complete',
  'tier_unlock',
  'stage_start',
  'stage_end',
  'stage_retry',
  'stage_replay',
  'replay_view',
  /*
   * 클리어한 결과 카드를 **실제로 조작할 수 있게 된** 시점 (TASK-2026-002 AC-04).
   * 클리어 순간과 다르다 — 그 사이에 개봉 연출이 있고, 그동안 참여자는 아무
   * 것도 할 수 없다. 재도전까지의 공백을 해석하려면 두 시각이 모두 필요하다.
   */
  'result_ready',
] as const;

export type TelemetryEventName = (typeof TELEMETRY_EVENT_NAMES)[number];

/**
 * 페이로드에 담을 수 있는 값. 자유 문자열을 원천적으로 막는다.
 *
 * 숫자 배열은 `CR-04`의 리플레이 기록(`deltas_ms`) 때문에 열려 있다. 타입이
 * 허용한다고 아무 키나 배열을 받는 것은 아니다 — 검사기가 `NUMBER_ARRAY_FIELDS`에
 * 있는 키만, 그것도 유한한 정수만 통과시킨다.
 */
export type TelemetryValue = number | boolean | string | readonly number[];

export type TelemetryPayload = Readonly<Record<string, TelemetryValue>>;

/** 한 page 실행 안에서만 비교할 수 있는 단조 시계 표식 (TASK-2026-003/004 CR-02). */
export interface EventClock {
  readonly segmentId: string;
  readonly elapsedMs: number;
}

export interface TelemetryEvent {
  readonly name: TelemetryEventName;
  /** epoch ms. 사용자 식별자가 아니다. */
  readonly at: number;
  /** page 실행 식별자. `client_elapsed_ms`와 항상 함께 있거나 함께 없다. */
  readonly clock_segment_id?: string;
  /** 같은 clock segment 안에서만 시간 차에 쓰는 monotonic elapsed ms. */
  readonly client_elapsed_ms?: number;
  /**
   * 저장 순번 (SPEC 「포함 범위」 4의 event sequence).
   *
   * 시계가 아니라 순서를 보증한다. `at`은 같은 밀리초에 여러 건이 겹치고,
   * 기기 시각이 바뀌면 뒤로 돌기도 한다. 사후에 "무엇이 먼저였나"를 다투지
   * 않으려면 단조 증가하는 값이 따로 있어야 한다. 저장할 때 로그가 매긴다.
   *
   * 만드는 시점에는 없다(`createEvent`는 붙이지 않는다). 옛 기록에도 없어
   * 선택 항목이다.
   */
  readonly seq?: number;
  readonly payload: TelemetryPayload;
}

/**
 * 이벤트별로 허용하는 페이로드 키. 목록 밖 키는 거부한다.
 * `stage_end`의 `moves`·`duration_ms`가 명세가 말한 "요약값"이다.
 */
export const ALLOWED_FIELDS: Readonly<Record<TelemetryEventName, readonly string[]>> = Object.freeze({
  app_open: Object.freeze([]),
  how_to_play_view: Object.freeze([]),
  /*
   * 안내 화면에 머문 시간 (`DEC-044`).
   *
   * 온라인 진행에는 관찰자가 없어 이 값을 사람이 잴 수 없다. 규칙을 읽었는지
   * 그냥 넘겼는지는 이 숫자와 `how_to_play_skip` 여부로만 판단한다.
   */
  how_to_play_complete: Object.freeze(['duration_ms']),
  how_to_play_skip: Object.freeze(['duration_ms']),
  tutorial_start: Object.freeze(['stage_id', 'tier']),
  tutorial_complete: Object.freeze(['stage_id', 'tier', 'moves', 'duration_ms']),
  tier_unlock: Object.freeze(['tier']),
  /*
   * `attempt`는 스테이지 안에서 1부터 증가하는 시도 번호다(TASK-2026-002 AC-02).
   * 이것이 없으면 여러 시도의 시작·종료·경로를 사후에 짝지을 수 없다. 번호일
   * 뿐 참여자 식별자가 아니다 — 같은 번호가 모든 참여자에게 나온다.
   */
  stage_start: Object.freeze(['stage_id', 'tier', 'attempt', 'attempt_id', 'previous_attempt_id']),
  /*
   * `first_move_ms`·`first_open_ms`는 진행자 기록양식의 `firstMoveSec`·
   * `firstOpenSec`을 대신한다(`DEC-044`). 화면을 보고 첫 수를 두기까지, 첫
   * 조각을 열기까지 걸린 시간이다. 끝내 하지 못했으면 담지 않는다 — 0으로
   * 채우면 "즉시 했다"와 구분되지 않는다.
   *
   * `opened_targets`는 **목표 타일만** 센다. 예전에는 열린 칸 전체를 넣었는데,
   * 클리어하면 마무리 오픈으로 남은 타일이 모두 열리므로 사실상 스테이지의
   * 전체 타일 수가 들어가 있었다(Tier 1에서 2가 아니라 6). 장식은 섞지 않고
   * `opened_decorations`로 따로 센다(TASK-2026-002 AC-03).
   *
   * `rejected_inputs`는 보드 밖이라 거부된 입력 수다. 이동과 구별해야 "막힌
   * 방향을 반복해서 눌렀다"를 "가만히 있었다"와 나눠 읽을 수 있다.
   *
   * `first_open_ms`는 **원래 의미 그대로** 첫 타일(목표든 장식이든)을 연
   * 시각이다. 첫 목표만 보려면 `first_target_open_ms`를 쓴다. 한때 앞의
   * 필드를 목표 전용으로 바꿨다가 독립 리뷰(REV-003)에서 되돌렸다 — 이름은
   * 그대로 두고 뜻만 바꾸면 과거 기록과 새 기록을 구별할 방법이 없다.
   *
   * `paused_ms`는 도움말 등으로 판을 떠나 있던 시간이다. `duration_ms`에서
   * 이미 빠져 있고, 얼마나 떠나 있었는지를 따로 보기 위해 함께 남긴다.
   */
  stage_end: Object.freeze([
    'stage_id',
    'tier',
    'attempt',
    'attempt_id',
    'previous_attempt_id',
    'end_reason',
    'moves',
    'duration_ms',
    'cleared',
    'opened_targets',
    'opened_decorations',
    'rejected_inputs',
    'paused_ms',
    'first_move_ms',
    'first_open_ms',
    'first_target_open_ms',
  ]),
  /*
   * 끝까지 가지 못한 시도의 종료.
   *
   * `abandoned`는 사용자가 `다시 시작`을 누른 것과 그 밖의 이유로 시도가
   * 버려진 것을 가른다. `interrupted`는 그중에서도 브라우저를 닫거나
   * 새로고침해 끝이 제때 기록되지 못하고, 다음 실행이 checkpoint를 보고
   * 뒤늦게 닫은 경우다. 둘을 섞으면 "중간에 그만둔 사람"을 셀 수 없다.
   */
  stage_retry: Object.freeze([
    'stage_id',
    'tier',
    'attempt',
    'attempt_id',
    'previous_attempt_id',
    'end_reason',
    'moves',
    'duration_ms',
    'abandoned',
    'interrupted',
    'inactive_ms',
    'opened_targets',
    'opened_decorations',
    'rejected_inputs',
    'paused_ms',
    'first_move_ms',
    'first_open_ms',
    'first_target_open_ms',
  ]),
  stage_replay: Object.freeze([
    'stage_id',
    'tier',
    'attempt',
    'attempt_id',
    'path',
    'deltas_ms',
    'truncated',
  ]),
  /*
   * 리플레이를 본 기록. **경로를 다시 담지 않는다.**
   *
   * 재생 대상은 이미 `stage_replay`로 남아 있으므로, 여기서는 그것을
   * 가리키기만 한다(`source_at` = 원본 기록의 시각). 경로를 복제하면 같은
   * 경로가 저장소에 여러 벌 쌓이고, 어느 것이 실제 플레이인지 흐려진다.
   */
  replay_view: Object.freeze(['stage_id', 'tier', 'source_at', 'actor']),
  /** `delay_ms`는 클리어부터 조작 가능해지기까지의 공백이다. */
  result_ready: Object.freeze(['stage_id', 'tier', 'attempt', 'attempt_id', 'delay_ms']),
});

/**
 * 문자열을 허용하는 키. 각각 전용 패턴으로만 통과한다(`CR-04`).
 *
 * 여기 없는 키에 문자열이 들어오면 거부한다. 자유 입력이 로그로 새는 경로를
 * 하나도 열어 두지 않기 위한 최종 방어선이다.
 */
const STRING_PATTERNS: Readonly<Record<string, RegExp>> = Object.freeze({
  stage_id: /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/,
  /*
   * 이동 순서. 한 수당 한 글자다. 문자 집합이 `nsew` 넷뿐이라 숫자도 기호도
   * 한글도 담기지 않는다 — 전화번호나 이메일이 여기로 들어올 방법이 없다.
   */
  path: /^[nsew]*$/,
  /*
   * 누가 재생했는가. 온라인 진행에서는 아직 식별 수단이 없어 빈 값이다.
   * 나중에 참여자 번호를 넣더라도 `p01` 형태의 일련번호만 들어오도록
   * `stage_id`와 같은 모양으로 제한한다 — 이름이나 연락처가 들어갈 자리가
   * 아니다(`SPEC.md` 부록 A-4).
   */
  actor: /^$|^[a-z][a-z0-9]*(-[a-z0-9]+)*$/,
  /*
   * 시도 ID. 소문자 `a`로 시작하는 짧은 영숫자다(중앙 명세 F1-05).
   *
   * 참가자 식별자가 아니다. 시도마다 새로 만들고 재사용하지 않으며, 기기나
   * 사람을 가리키는 값을 재료로 쓰지 않는다. 문자 집합이 영숫자뿐이라
   * 이름·연락처가 이 자리에 들어올 방법이 없다.
   */
  attempt_id: /^a[0-9a-z]{4,40}$/,
  previous_attempt_id: /^a[0-9a-z]{4,40}$/,
  /*
   * 종료 사유. 중앙 명세가 고정한 다섯 값만 받는다 —— 패턴이 곧 enum이다.
   * 새 값을 넣으려면 이 파일을 고쳐야 하고, 그러면 리뷰에 걸린다.
   */
  end_reason: /^(clear|restart|tier_change|quit|interrupted)$/,
});

/** 문자열 필드별 길이 상한. */
const MAX_STRING_LENGTH: Readonly<Record<string, number>> = Object.freeze({
  stage_id: 64,
  actor: 32,
  attempt_id: 41,
  previous_attempt_id: 41,
  end_reason: 16,
  // 한 판이 2,000수를 넘을 이유가 없다. 무한히 굴려 저장소를 채우는 것을 막는다.
  path: 2000,
});

/**
 * 숫자 배열을 허용하는 키와 그 원소 제약(`CR-04`).
 *
 * 배열 일반을 허용하지 않는다. 여기 명시된 키만, 그것도 유한한 정수만 받는다.
 */
const NUMBER_ARRAY_FIELDS: Readonly<Record<string, { readonly max: number; readonly maxLength: number }>> =
  Object.freeze({
    // 각 수의 직전 수로부터의 경과(ms). 10분을 넘는 간격은 세션 이탈이라 담지 않는다.
    deltas_ms: Object.freeze({ max: 600_000, maxLength: 2000 }),
  });

/**
 * `stage_id` 형식: `{region_id}-{difficulty}-{sequence}` (SPEC S-5).
 *
 * **반드시 소문자 알파벳으로 시작해야 한다.** 처음에는 소문자·숫자·하이픈을
 * 모두 허용했는데, 그러면 `010-1234-5678` 같은 전화번호가 그대로 통과한다.
 * SPEC 부록 A-4는 전화번호를 식별값 재료로 쓰는 것을 금지하므로 실제 위반이며,
 * 테스트가 이를 잡아냈다. `region_id`는 항상 알파벳으로 시작하므로 이 제약이
 * 정상 값을 막지 않는다.
 *
 * 다만 이 패턴은 형식 검사일 뿐 완전한 보호수단이 아니다. 최종 방어선은
 * `ALLOWED_FIELDS` 허용 목록이며, 문자열을 받는 키는 `stage_id` 하나뿐이다.
 */
export const STAGE_ID_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

export function isTelemetryEventName(value: string): value is TelemetryEventName {
  return (TELEMETRY_EVENT_NAMES as readonly string[]).includes(value);
}

/**
 * 이벤트 하나를 검사한다. 위반을 전부 모아 돌려준다.
 * 빈 배열이면 유효하다.
 */
export function validateEvent(event: TelemetryEvent): readonly string[] {
  const errors: string[] = [];

  if (!isTelemetryEventName(event.name)) {
    errors.push(`알 수 없는 이벤트 이름입니다: ${String(event.name)}`);
    return errors;
  }

  if (!Number.isFinite(event.at)) {
    errors.push('시각(at)이 유효한 숫자가 아닙니다.');
  }

  const hasClockSegment = event.clock_segment_id !== undefined;
  const hasClientElapsed = event.client_elapsed_ms !== undefined;
  if (hasClockSegment !== hasClientElapsed) {
    errors.push('clock_segment_id와 client_elapsed_ms는 함께 있어야 합니다.');
  } else if (hasClockSegment && hasClientElapsed) {
    if (
      typeof event.clock_segment_id !== 'string' ||
      !/^[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/.test(event.clock_segment_id)
    ) {
      errors.push('clock_segment_id가 허용된 형식이 아닙니다.');
    }
    if (
      typeof event.client_elapsed_ms !== 'number' ||
      !Number.isSafeInteger(event.client_elapsed_ms) ||
      event.client_elapsed_ms < 0 ||
      event.client_elapsed_ms > 7 * 24 * 60 * 60 * 1000
    ) {
      errors.push('client_elapsed_ms는 0 이상 7일 이하의 안전한 정수여야 합니다.');
    }
  }

  /*
   * 순번이 있다면 **양의 안전 정수**여야 한다 (REV-011).
   *
   * 예전에는 봉투의 `seq`를 전혀 보지 않았다. 저장소에 `seq: "x"`를 심어
   * 두면 그대로 통과했고, 다음 이벤트의 순번이 `"x" + 1` = `"x1"`, 그다음이
   * `"x11"`이 되어 **그 뒤의 로그 전체가 오염**됐다. 저장소는 사용자가 직접
   * 고칠 수 있으므로 값의 모양을 여기서 못박는다.
   */
  if (event.seq !== undefined) {
    if (typeof event.seq !== 'number' || !Number.isSafeInteger(event.seq) || event.seq < 1) {
      errors.push('순번(seq)은 1 이상의 안전한 정수여야 합니다.');
    }
  }

  const allowed = ALLOWED_FIELDS[event.name];

  for (const [key, value] of Object.entries(event.payload)) {
    if (!allowed.includes(key)) {
      errors.push(`${event.name}: 허용되지 않은 필드 '${key}'`);
      continue;
    }

    if (typeof value === 'string') {
      const pattern = STRING_PATTERNS[key];
      if (pattern === undefined) {
        errors.push(`${event.name}: '${key}'는 문자열을 담을 수 없습니다(자유 입력 금지).`);
        continue;
      }
      if (value.length > (MAX_STRING_LENGTH[key] ?? 0) || !pattern.test(value)) {
        errors.push(`${event.name}: '${key}' 값이 허용된 형식이 아닙니다.`);
      }
      continue;
    }

    if (Array.isArray(value)) {
      const rule = NUMBER_ARRAY_FIELDS[key];
      if (rule === undefined) {
        errors.push(`${event.name}: '${key}'는 배열을 담을 수 없습니다.`);
        continue;
      }
      if (value.length > rule.maxLength) {
        errors.push(`${event.name}: '${key}'의 길이가 상한(${rule.maxLength})을 넘습니다.`);
        continue;
      }
      const bad = value.some(
        (item) => typeof item !== 'number' || !Number.isInteger(item) || item < 0 || item > rule.max,
      );
      if (bad) {
        errors.push(`${event.name}: '${key}'는 0 이상 ${rule.max} 이하의 정수만 담을 수 있습니다.`);
      }
      continue;
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        errors.push(`${event.name}: '${key}'가 유한한 숫자가 아닙니다.`);
      }
      continue;
    }

    if (typeof value !== 'boolean') {
      errors.push(`${event.name}: '${key}'의 타입을 담을 수 없습니다.`);
    }
  }

  /*
   * 재생하려면 경로와 시간이 한 수씩 짝지어야 한다. 어긋난 기록은 재생이
   * 성립하지 않으므로 저장 시점에 거른다(`CR-04` §4).
   */
  if (event.name === 'stage_replay') {
    const path = event.payload['path'];
    const deltas = event.payload['deltas_ms'];
    if (typeof path === 'string' && Array.isArray(deltas) && path.length !== deltas.length) {
      errors.push(
        `stage_replay: path(${path.length}수)와 deltas_ms(${deltas.length}개)의 길이가 다릅니다.`,
      );
    }
  }

  return errors;
}

/**
 * 검사를 통과한 이벤트를 만든다. 위반이 있으면 던진다 —
 * 잘못된 이벤트가 조용히 로그에 쌓이는 편보다 즉시 드러나는 편이 낫다.
 */
export function createEvent(
  name: TelemetryEventName,
  payload: TelemetryPayload = {},
  at: number = Date.now(),
  clock?: EventClock,
): TelemetryEvent {
  const event: TelemetryEvent = Object.freeze({
    name,
    at,
    ...(clock === undefined
      ? {}
      : { clock_segment_id: clock.segmentId, client_elapsed_ms: Math.round(clock.elapsedMs) }),
    payload: Object.freeze({ ...payload }),
  });
  const errors = validateEvent(event);

  if (errors.length > 0) {
    throw new Error(`계측 이벤트가 스키마를 위반했습니다: ${errors.join(' / ')}`);
  }

  return event;
}
